import { useMutation } from '@apollo/client'
import { Button, Checkbox, Col, Form, InputNumber, message, Row, Spin } from 'antd'
import * as PropTypes from 'prop-types'
import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLoginModal } from '../../../../../context/loginModalContext'
import { showAuthError } from '../../../../../shared/utils/webHelpers'
import { StyledDrawer } from '../../../../common/CommonStyles'
import { v4 as uuidv4 } from 'uuid'
import { useForceUpdate } from '../../../../../shared/hooks/useForceUpdate'
import CreateNewItemExtraButtons from '../../../../common/CreateNewItemExtraButtons'
import { useNavigate } from 'react-router-dom'
import ADD_NEW_LEAD_STAGE from '@dentolize/clinic-mobile/src/shared/store/mutations/actions/leads/addNewLeadStage'
import EDIT_LEAD_STAGE from '@dentolize/clinic-mobile/src/shared/store/mutations/actions/leads/editLeadStage'
import NameField from '../../../../common/formFields/NameField'
import UserColorPicker from '../../Users/UserColorPicker'
import DeleteLeadStageButton from './DeleteLeadStageButton'
import SearchField from '../../../../common/searchFields/SearchField'
import { SEARCH_USERS_QUERY } from '@dentolize/clinic-mobile/src/shared/store/queries/appointmentQueries'

const LeadStageForm = props => {
  const [forceUpdate, handleForceUpdate] = useForceUpdate()

  const navigate = useNavigate()
  const [form] = Form.useForm()
  const { setLoginModalVisible } = useLoginModal()
  const { t } = useTranslation()

  const { formVisible, selectedItem, handleClose } = props
  const { validateFields, resetFields, setFieldsValue } = form

  useEffect(() => {
    if (formVisible) {
      resetFields()
      if (selectedItem.new) {
        setFieldsValue({
          color: `#${Math.floor(Math.random() * 16777215).toString(16)}`
        })
      }
    }
  }, [formVisible])

  const [addNewItem, { loading }] = useMutation(ADD_NEW_LEAD_STAGE)
  const [editItem, { loading: loadingEdit }] = useMutation(EDIT_LEAD_STAGE)

  const generatedID = useMemo(uuidv4, [formVisible, forceUpdate])

  const handleSubmit = key => {
    validateFields()
      .then(async values => {
        try {
          let item

          if (selectedItem.new) {
            item = await addNewItem({
              variables: {
                ...values,
                users: values.users?.map(u => u.key) ?? [],
                generatedID
              }
            })

            if (key === 'view') {
              navigate(`/settings/leads/stage/${generatedID}`)
              handleClose()
            } else if (key === 'new') {
              resetFields()
              handleForceUpdate()
              setFieldsValue({
                color: `#${Math.floor(Math.random() * 16777215).toString(16)}`
              })
            } else {
              handleClose(item.data.addNewLeadStage)
            }
          } else {
            item = await editItem({
              variables: {
                ...values,
                users: values.users?.map(u => u.key) ?? [],
                leadStage: selectedItem.id
              }
            })
            handleClose()
          }

          message.success(`${selectedItem.new ? t('common.createdSuccessfully') : t('common.updatedSuccessfully')}`)
          if (props.handleAnimateRow) {
            props.handleAnimateRow(
              (item.data.addNewLeadStage && item.data.addNewLeadStage.id) ||
                (item.data.editLeadStage && item.data.editLeadStage.id),
              selectedItem.new
            )
          }
        } catch (e) {
          showAuthError(e, t, setLoginModalVisible)
        }
      })
      .catch(({ errorFields }) => {
        form.scrollToField(errorFields?.[0]?.name)
      })
  }

  return (
    <StyledDrawer
      title={!selectedItem.new ? t('common.edit') : t('patients.newItem')}
      open={formVisible}
      onClose={handleClose}
      destroyOnClose
      footer={
        <div className="flex-container justify-between">
          {!selectedItem.new ? (
            <DeleteLeadStageButton
              selectedItem={selectedItem}
              handleAnimateRow={props.handleAnimateRow}
              handleClose={handleClose}
            />
          ) : selectedItem.new && selectedItem.fromDrawer ? (
            <CreateNewItemExtraButtons handleSubmit={handleSubmit} disabled={loading} />
          ) : null}
          <Button loading={loading || loadingEdit} htmlType="submit" type="primary" form={`${generatedID}LeadStageForm`}>
            {selectedItem.new ? t('common.create') : t('common.update')}
          </Button>
        </div>
      }
    >
      <Spin spinning={loading || loadingEdit}>
        <Form
          layout="vertical"
          scrollToFirstError
          form={form}
          name={`${generatedID}LeadStageForm`}
          onFinish={handleSubmit}
          initialValues={{
            ...selectedItem,
            users: selectedItem.users
              ? selectedItem.users.map(u => ({
                  key: u.id,
                  label: u.name
                }))
              : []
          }}
        >
          <Row type="flex" gutter={16}>
            <Col span={24}>
              <NameField required min={1} max={100} autoFocus autoComplete="" />
            </Col>
            <Col span={12}>
              <Form.Item name="color" label={t('settings.groups.color')}>
                <UserColorPicker />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="dueDays" label={t('app.dueDays')} extra={t('app.dueDaysInfo')}>
                <InputNumber precision={0} max={999999} min={0} addonAfter={t('settings.patientReminders.days')} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <SearchField
                GET_QUERY={SEARCH_USERS_QUERY}
                label={t('calendar.assignedTo')}
                fieldName="users"
                dataName="searchUsers"
                allowClear
                multiple
              />
            </Col>
            <Col span={24}>
              <Form.Item name="requireReject" valuePropName="checked">
                <Checkbox>{t('app.requireReject')}</Checkbox>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Spin>
    </StyledDrawer>
  )
}

LeadStageForm.propTypes = {
  selectedItem: PropTypes.object.isRequired,
  handleClose: PropTypes.func.isRequired,
  formVisible: PropTypes.bool.isRequired,
  handleAnimateRow: PropTypes.func
}

export default LeadStageForm
